import { Injectable } from '@angular/core';
import { CanvasService } from './canvas.service';
import { BatchCommand, InsertElementCommand } from '@svgeditor/history.js';
import { SelectedElementsService } from './selected-elements.service';
import { ElementSettingsService } from './element-settings.service';
import { CanvasExtensionsService } from './canvas-extensions.service';

@Injectable({ providedIn: 'root' })
export class PasteService {
    private copiedElements;

    constructor(
        private canvasService: CanvasService,
        private selectedElementsService: SelectedElementsService,
        private elementSettingsService: ElementSettingsService,
        private extensions: CanvasExtensionsService
    ) {}

    async duplicateElements() {
        const canvas = this.canvasService.getCanvas();
        const selectedElements = this.selectedElementsService.getSelectedElements();
        if (selectedElements.length === 0) return;
        const data = selectedElements.map(x => canvas.getJsonFromSvgElements(x));
        await this.paste(data);
    }

    copyElements() {
        const canvas = this.canvasService.getCanvas();
        const selectedElements = this.selectedElementsService.getSelectedElements();
        if (selectedElements.length === 0) return;
        this.copiedElements = selectedElements.map(x => canvas.getJsonFromSvgElements(x));
    }

    public async pasteCopiedElements() {
        return (await this.safePasteFromJson(this.copiedElements)) as SVGGraphicsElement[];
    }

    public async paste(objectJson: any[]) {
        return (await this.safePasteFromJson(objectJson)) as SVGGraphicsElement[];
    }

    private async safePasteFromJson(json: any) {
        let clipb = json;

        if (typeof json === 'string') {
            clipb = JSON.parse(json);
        }

        if (!clipb) return;
        let len = clipb.length;
        if (!len) return;

        const canvas = this.canvasService.getCanvas();
        const pasted = [];
        const batchCmd = new BatchCommand('Paste elements');

        const changedIDs = {};

        const checkIDs = elem => {
            let srcid;

            if (elem.attr && elem.attr.id) {
                srcid = elem.attr.id;
                const newId = canvas.getNextId();
                changedIDs[elem.attr.id] = newId;
                elem.attr.id = changedIDs[elem.attr.id];
            }

            if (elem.children && elem.children.length > 0) {
                elem.children.forEach(child => checkIDs(child));
            }
        };
        clipb.forEach(elem => checkIDs(elem));

        canvas
            .runExtensions(
                'IDsUpdated',
                /** @type {module:svgcanvas.SvgCanvas#event:ext_IDsUpdated} */
                { elems: clipb, changes: changedIDs },
                true
            )
            .forEach(function (extChanges) {
                if (!extChanges || !('remove' in extChanges)) return;

                extChanges.remove.forEach(function (removeID) {
                    clipb = clipb.filter(function (clipBoardItem) {
                        return clipBoardItem.attr.id !== removeID;
                    });
                });
            });

        while (len--) {
            const elem = clipb[len];
            if (!elem) {
                continue;
            }
            if (!isNaN(elem.attr.x)) elem.attr.x = (Number(elem.attr.x) + 50).toString();
            if (!isNaN(elem.attr.y)) elem.attr.y = (Number(elem.attr.y) + 50).toString();

            const copy = canvas.addSVGElementsFromJson(elem);
            if (copy.tagName == 'foreignObject') {
                this.extensions.attachEventListeners(copy);
            }
            pasted.push(copy);
            batchCmd.addSubCommand(new InsertElementCommand(copy));

            canvas.restoreRefElements(copy);
        }

        canvas.selectOnly(pasted);
        canvas.addCommandToHistory(batchCmd);
        canvas.call('changed', pasted);

        await this.elementSettingsService.duplicate(changedIDs);

        return pasted;
    }
}
